import { ApiError } from './api';
import { readLifecycleAttribution } from './lifecycle-attribution';

export type FunnelEvent = 'package_question_context_present' | 'package_default_offer_impression' | 'package_cta_visible' | 'package_sheet_dismissed' | 'receipt_contact_required' | 'receipt_contact_submitted' | 'receipt_contact_invalid' | 'checkout_resumed_after_receipt_contact' | 'payment_returned_to_app' | 'mini_app_open' | 'paywall_viewed' | 'offers_impression' | 'package_chooser_opened'
  | 'package_selected' | 'purchase_cta_clicked' | 'checkout_request_started' | 'payment_redirect_opened'
  | 'daily_forecast_push_opened'
  | 'report_catalog_viewed'
  | 'product_viewed' | 'offer_selected' | 'checkout_viewed' | 'payment_button_clicked'
  | 'checkout_error' | 'payment_redirect_started'
  | 'compatibility_opened' | 'compatibility_partner_form_started' | 'compatibility_partner_form_completed'
  | 'compatibility_checkout_opened' | 'compatibility_report_opened'
  | 'free_intro_viewed' | 'free_intro_continuation_impression' | 'free_intro_continuation_clicked'
  | 'daily_forecast_viewed' | 'daily_continuation_impression' | 'daily_continuation_clicked'
  | 'free_question_answer_viewed' | 'free_question_continuation_impression' | 'free_question_continuation_clicked'
  | 'chat_opened_from_continuation'
  | 'intro_number_provenance_impression' | 'intro_number_trace_opened' | 'intro_number_provenance_continue'
  | 'numbers_explainer_opened' | 'numbers_explainer_item_viewed'
  | 'numbers_explainer_primary_question_impression' | 'numbers_explainer_primary_question_clicked'
  | 'chat_opened_from_numbers_explainer';

export type ContinuationSurface = 'introductory_reading' | 'daily_forecast' | 'free_question' | 'chat' | 'numbers_explainer';
export type EntitlementAnalyticsState = 'free_available' | 'paid_balance' | 'none';

const trackedOnce = new Set<string>();

/** Await before a full-page redirect, but never block a purchase on analytics. */
export async function trackFunnel(event: FunnelEvent, payload: {
  productId?: string; source?: 'paywall' | 'offers' | 'report' | 'checkout'; code?: string; status?: number;
  sourceSurface?: ContinuationSurface; angleType?: string; entitlementState?: EntitlementAnalyticsState;
  questionContextPresent?: boolean; chooserVersion?: number; elapsedMs?: number;
  questionRank?: 'primary' | 'secondary';
  numberType?: string; intentCategory?: string;
  sourceCampaign?: string; campaignStep?: number; variantId?: string;
} = {}): Promise<void> {
  const attribution = readLifecycleAttribution();
  const body = {
    ...payload, monetizationVersion: 3,
    ...(attribution && !payload.sourceCampaign ? {
      sourceCampaign: attribution.sourceCampaign,
      campaignStep: attribution.campaignStep,
      variantId: attribution.variantId,
    } : {}),
  };
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 1500);
  try {
    await fetch('/v1/analytics/events', {
      method: 'POST', credentials: 'include', keepalive: true, signal: controller.signal,
      headers: { 'content-type': 'application/json' }, body: JSON.stringify({ event, payload: body }),
    });
  } catch {
    // Measurement is best effort; commerce and navigation stay available.
  } finally {
    window.clearTimeout(timeout);
  }
}

export function trackFunnelOnce(key: string, event: FunnelEvent, payload: Parameters<typeof trackFunnel>[1] = {}): void {
  if (trackedOnce.has(key)) return;
  trackedOnce.add(key);
  void trackFunnel(event, payload);
}

export function trackCheckoutError(productId: string, error: unknown): void {
  void trackFunnel('checkout_error', {
    productId, source: 'checkout',
    code: error instanceof ApiError ? error.code : 'CLIENT_ERROR',
    status: error instanceof ApiError ? error.status : 0,
  });
}
